import React from "react";
import Head from "next/head";
import Link from "next/link";
import Image from "next/image";
import { BASE_ASSETS_URL } from "@constants/constants";
import { RxCross1 } from "react-icons/rx";
import FaqCard from "@components/Cards/FaqCard";
import { createUTMQueryParams } from "@utils/signature_owner";

const frustrations = [
  {
    title: "Repetitive Data Entry",
    description: "Filling the same details over and over again.",
  },
  {
    title: "Complicated Legal Terms",
    description: "No explanations, just confusing jargon.",
  },
  {
    title: "Tedious Template Setup",
    description: "Manually creating forms and templates takes forever.",
  },
  {
    title: "Limited Automation & Integrations",
    description: "Struggles to connect with CRMs & workflows.",
  },
];

const features = [
  {
    title: "Map My Fields",
    description: `AI automatically detects fields inside documents and places them in the right spots for a seamless and error-free signing experience.`,
    image: "map-my-fields.png",
  },
  {
    title: "Intelligent Template Creation",
    description: `Upload any signed contract, and our AI will instantly replace variables, creating a fresh, reusable PDF template ready to send to new signers.`,
    image: "intelligent-template-creation.png",
  },
  {
    title: "Dynamic Templates",
    description: `Experience truly intelligent documents that adapt to each signer and situation, automatically customizing content based on context.`,
    image: "dynamic-templates.png",
  },
  {
    title: "AI Document Assistant",
    description: `Navigate complex documents with our voice-enabled AI assistant. Get plain-English explanations of complicated terms and conditions as you go.`,
    image: "ai-document-assistant.png",
  },
  {
    title: "AI Signature Form Creation",
    description: `Simply describe the form you need in plain English, and our AI will generate it instantly. No more struggling with form builders or starting from scratch.`,
    image: "ai-signature-form-creation.png",
  },
];

const PlatformComparison = [
  {
    feature: "Template Generation",
    boloSign: "✅ (Instant AI-powered generation)",
    docuSign: "❌",
    adobeSign: "❌",
  },
  {
    feature: "Smart Auto-Fill",
    boloSign: "✅ (Smart contextual auto-fill)",
    docuSign: "❌",
    adobeSign: "Limited auto-fill",
  },
  {
    feature: "Document Assistant",
    boloSign: "✅ (Interactive AI assistant)",
    docuSign: "❌",
    adobeSign: "❌",
  },
  {
    feature: "Dynamic Templates",
    boloSign: "✅",
    docuSign: "❌",
    adobeSign: "❌",
  },
  {
    feature: "Pricing",
    boloSign: "✅ (AI Included for Free, Affordable Plans)",
    docuSign: "❌ (Expensive, No AI Features)",
    adobeSign: "❌ (Expensive, No AI Features)",
  },
  {
    feature: "AI Signature Form Creation",
    boloSign: "✅ (AI-driven form generation)",
    docuSign: "❌",
    adobeSign: "❌",
  },
];

const Faqs = [
  {
    faqQuestion: "Is BoloSign legally binding?",
    faqAnswer:
      "Yes, BoloSign complies with e-signature laws and regulations, ensuring your documents are legally binding. We are compliant with all the norms of UETA, ISO, HIPAA, ETA, etc.",
  },
  {
    faqQuestion: "How does BoloSign AI make signing documents easier?",
    faqAnswer:
      "BoloSign AI automates form creation, auto-fills information, explains legal terms, and ensures a seamless signing experience.",
  },
  {
    faqQuestion: "Can I migrate from DocuSign, PandaDoc, or Adobe Sign?",
    faqAnswer:
      "Yes! With one-click AI migration, all your documents, templates, and signer details are transferred instantly.",
  },
  {
    faqQuestion: "Do I need technical knowledge to use BoloSign AI?",
    faqAnswer:
      "Not at all! Our platform is designed to be user-friendly and intuitive, even for first-time users.",
  },
  {
    faqQuestion: "Can I create my own contract templates with AI?",
    faqAnswer:
      "Yes! BoloSign AI can generate templates automatically from any document and adapt them to different signers.",
  },
];

// bg gradient 135 deg #2a0e5b to #6120ff

const AI = () => {
  return (
    <div className="w-full">
      <Head>
        <title>AI Powered eSignatures | BoloSign</title>
        <meta
          name="description"
          content="Experience the power of AI in document signing. Faster, smarter, and more intuitive than ever before."
        />
        <link
          rel="icon"
          href="https://boloforms-internal-images.s3.ap-south-1.amazonaws.com/website/assets/v2/clm/Logo.png"
        />
        <meta
          name="keywords"
          content="eSignatures, AI eSignatures, BoloSign, BoloForms, eSignature platform, eSignature solution"
        />

        {/* <!-- Facebook Meta Tags --> */}
        <meta property="og:url" content="https://www.bolosign.com/ai" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="AI Powered eSignatures | BoloSign" />
        <meta
          property="og:description"
          content="Experience the power of AI in document signing. Faster, smarter, and more intuitive than ever before."
        />
        <meta
          property="og:image"
          content="https://boloforms-internal-images.s3.ap-south-1.amazonaws.com/website/assets/v2/clm/media-viewport.png"
        />

        {/* <!-- Twitter Meta Tags --> */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          property="twitter:domain"
          content="https://www.bolosign.com/ai"
        />
        <meta property="twitter:url" content="https://www.bolosign.com/ai" />
        <meta
          name="twitter:title"
          content="AI Powered eSignatures | BoloSign"
        />
        <meta
          name="twitter:description"
          content="Experience the power of AI in document signing. Faster, smarter, and more intuitive than ever before."
        />
        <meta
          name="twitter:image"
          content="https://boloforms-internal-images.s3.ap-south-1.amazonaws.com/website/assets/v2/clm/media-viewport.png"
        />

        <link rel="canonical" href="https://www.bolosign.com/ai" />
      </Head>
      <section
        className="w-full relative pt-8 pb-20 md:py-24 mt-[80px] sm:mt-[98px]"
        style={{ background: "linear-gradient(135deg, #2a0e5b, #6120ff)" }}
      >
        <div className="w-full max-w-screen-txl mx-auto flex flex-col md:flex-row justify-between items-start gap-8 md:gap-4 lg:gap-20 px-4">
          <div className="flex-1 flex justify-center flex-col items-center relative z-10 gap-4">
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white">
              The Future of eSignatures is Here
            </h1>
            <p className="text-lg sm:text-xl lg:text-2xl text-white">
              Experience the power of AI in document signing. Faster, smarter,
              and more intuitive than ever before.
            </p>
            <div className="w-full mt-8">
              <Link
                href={`https://signature.boloforms.com/register?from=AI_PAGE${createUTMQueryParams(
                  {
                    from: "AI_PAGE",
                  }
                )}`}
                className="w-fit bg-white text-purple-800 px-10 py-3 rounded-lg text-2xl font-medium"
              >
                Get Started
              </Link>
            </div>
          </div>
          {/* <div className="w-full sm:w-[400px] lg:w-[650px] relative z-10"> */}
          <div className="w-full sm:w-[400px] lg:w-[40%] relative z-10">
            <Image
              src={`${BASE_ASSETS_URL}/v2/ai/how-to-use-ai-bolosign.gif`}
              // src={`${BASE_ASSETS_URL}/ai/how-to-use-ai-bolosign.gif`}
              className="w-full h-auto"
              width={10}
              height={10}
              loading="lazy"
              alt=""
            />
          </div>
          <div className="">
            <Image
              src={`${BASE_ASSETS_URL}/website/assets/v2/ai/headerBg.png`}
              // src={`${BASE_ASSETS_URL}/ai/headerBg.png`}
              className="w-3/4 h-auto xl:w-auto absolute bottom-0 right-0 xl:h-[65%] z-0"
              width={200}
              height={200}
              loading="lazy"
              alt=""
            />
          </div>
        </div>
      </section>

      <FrustrationFix data={frustrations} />

      <Features features={features} />

      <section
        className="py-16 sm:py-32"
        style={{ background: "linear-gradient(135deg, #2a0e5b, #6120ff)" }}
      >
        <ComparisonTable comparisonData={PlatformComparison} />
      </section>

      <section className="w-full py-12 md:py-20">
        <div className="p-4 max-w-screen-xl mx-auto w-full flex justify-center flex-col items-center relative z-10 gap-4 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-black">
            Currently Using Another eSignature Platform?
          </h1>
          <p className="text-lg max-w-screen-lg mx-auto sm:text-xl lg:text-2xl text-black">
            {`Whether you're using DocuSign, PandaDoc, Adobe Sign, or any other platform, switching to BoloSign is seamless. Our AI handles the migration while you enjoy the benefits of a more powerful, affordable solution.`}
          </p>
          <div className="w-full mt-8 mx-auto">
            <Link
              href={`https://signature.boloforms.com/register?from=AI_PAGE${createUTMQueryParams(
                {
                  from: "AI_PAGE",
                }
              )}`}
              className="w-fit text-white px-10 py-3 rounded-lg text-2xl font-medium"
              style={{
                background:
                  "linear-gradient(135deg, #552bc2, #ae63ff, #a4bdfe)",
              }}
            >
              Start Your Migration
            </Link>
          </div>
        </div>
      </section>

      {/* faqs  */}
      <section className="w-full px-4 py-8 sm:py-12">
        <div className="max-w-screen-lg mx-auto w-full flex justify-start flex-col items-start gap-4">
          <h1 className="text-4xl sm:text-5xl font-bold text-black mb-2 sm:mb-8">
            Frequently Asked Question
          </h1>
          <div className="w-full flex flex-col gap-4">
            {Faqs.map((faq, index) => (
              <FaqCard key={index} {...faq} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default AI;

export const ComparisonTable = ({ comparisonData }) => {
  return (
    <section className="max-w-screen-xl mx-auto px-4 text-center flex flex-col">
      <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold mb-12 text-white">
        Platform Comparison
      </h1>
      <div className="overflow-x-auto w-full max-w-screen-xl mx-auto">
        <table className="w-full rounded-2xl bg-white">
          <thead>
            <tr className="text-center text-base md:text-xl">
              <th className="px-2 py-4 border-[2px] border-blue-900">
                Features
              </th>
              <th className="px-2 py-4 border-[2px] border-blue-900">
                BoloSign AI
              </th>
              <th className="px-2 py-4 border-[2px] border-blue-900">
                DocuSign
              </th>
              <th className="px-2 py-4 border-[2px] border-blue-900">
                Adobe Sign
              </th>
            </tr>
          </thead>
          <tbody className="">
            {comparisonData.map((row, index) => (
              <tr key={index} className="text-center text-sm md:text-lg">
                <td className="px-2 py-4 border-[2px] border-blue-900 font-semibold">
                  {row.feature}
                </td>
                <td className="px-2 py-4 border-[2px] border-blue-900">
                  {row.boloSign}
                </td>
                <td className="px-2 py-4 border-[2px] border-blue-900 ">
                  {row.docuSign}
                </td>
                <td className="px-2 py-4 border-[2px] border-blue-900 ">
                  {row.adobeSign}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export const Features = ({ heading, description, features }) => {
  return (
    <div className="w-full max-w-screen-mxl mx-auto flex flex-col justify-center items-center gap-8 sm:gap-12 px-4 lg:px-8">
      <div className="w-full max-w-screen-lg mx-auto text-left sm:text-center">
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold ">
          {`Why You'll Love BoloSign AI`}
        </h1>
      </div>
      <div className="w-full flex flex-col gap-12 sm:gap-20">
        <div className="w-full flex flex-col gap-16">
          {features?.map((feature, index) => (
            <div
              key={index}
              className={`w-full flex flex-col justify-between items-center gap-4 sm:gap-20 ${
                index % 2 === 0 ? "sm:flex-row-reverse" : "sm:flex-row"
              }`}
            >
              <div className="w-full flex flex-col gap-3 sm:gap-6">
                {feature.title && (
                  <h1 className="text-2xl sm:text-4xl lg:text-5xl font-semibold md:font-bold">
                    {feature.title}
                  </h1>
                )}
                <p
                  className="text-lg sm:text-xl lg:text-2xl"
                  dangerouslySetInnerHTML={{ __html: feature.description }}
                ></p>
              </div>
              {feature.image && (
                <div className="w-full">
                  <Image
                    src={`${BASE_ASSETS_URL}/v2/ai/${feature.image}`}
                    // src={`${BASE_ASSETS_URL}/v2/gsuite/${feature.image}`}
                    className="w-full h-auto"
                    width={10}
                    height={10}
                    loading="lazy"
                    alt=""
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const FrustrationFix = ({ data }) => {
  return (
    <section className="max-w-screen-xl mx-auto px-4 text-center py-16 sm:py-20 flex flex-col">
      <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold ">
        Why Traditional eSignatures Are Frustrating?
      </h1>
      <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-8 md:gap-4 mt-10 sm:mt-16">
        {data.map((item, index) => (
          <div
            key={index}
            className="bg-[#fee5de] px-4 py-6 rounded-md relative"
          >
            <div className="text-lg h-10 w-10 bg-red-500 rounded-full text-white flex justify-center items-center absolute -top-5 left-1/2 -translate-x-1/2">
              <RxCross1 />
            </div>
            <h3 className="font-bold mt-2 text-xl">{item.title}</h3>
            <p className="text-lg mt-2">{item.description}</p>
          </div>
        ))}
      </div>
      <div className="mt-8 flex justify-center items-center flex-col">
        <Image
          src={`${BASE_ASSETS_URL}/v2/ai/robot.gif`}
          alt="AI Robot"
          className="w-full h-auto sm:w-auto sm:h-48"
          width={200}
          height={200}
          loading="lazy"
        />
      </div>
    </section>
  );
};
